const UTILIDAD_POR_DEFECTO = 0.7;

/**
 * Genera todas las claves de quincena en un rango de fechas dado.
 *
 * @param {Date} start - Fecha de inicio del rango.
 * @param {Date} end - Fecha de fin del rango.
 * @returns {string[]} Un array con las claves de quincena en formato 'año-mes-quincena' (por ejemplo, '2022-03-1').
 */

export const groupDataPorMes = (data) =>
  data.reduce((accumulator, envio) => {
    const envioDate = envio.created_at.toDate();
    const year = envioDate.getFullYear();
    const month = envioDate.getMonth();

    const key = `${year}-${month}`;

    if (!accumulator[key]) {
      accumulator[key] = [];
    }

    accumulator[key].push(envio);

    return accumulator;
  }, {});

/**
 * Calcula las utilidades por mes a partir de los envíos.
 *
 * @param {Object} enviosPorMes - Un objeto que contiene los envíos agrupados por mes.
 * @returns {Array} Un array con las utilidades calculadas por mes.
 */
export const calcularUtilidadesPorMes = (enviosPorMes) =>
  Object.values(enviosPorMes).map((envios) =>
    envios.reduce(
      (accumulator, envio) => (Number.isNaN(envio.utilidad) ? accumulator : accumulator + envio.utilidad),
      0
    )
  );

/**
 * Calcula las utilidades por quincena a partir de los envíos.
 *
 * @param {Object} enviosPorQuincena - Un objeto que contiene los envíos agrupados por quincena.
 * @returns {Array} Un array con las utilidades calculadas por quincena.
 */

export const calcularUtilidadesPorQuincenaTotales = (enviosPorQuincena) =>
  Object.values(enviosPorQuincena).map((envios) => {
    if (envios.length === 0) {
      return 0;
    }

    return envios.reduce(
      (accumulator, envio) => (Number.isNaN(envio.utilidad) ? accumulator : accumulator + envio.utilidad),
      0
    );
  });

// export const calcularUtilidadesPorQuincenaDagPacket = (enviosPorQuincena) =>
//   Object.values(enviosPorQuincena).map((envios) => {
//     if (envios.length === 0) {
//       return 0;
//     }

//     const totalUtilidad = envios.reduce((accumulator, envio) => {
//       const utilidad = envio.utilidadEnvioDagpacket ? envio.utilidadEnvioDagpacket : envio.utilidad * 0.3;
//       return Number.isNaN(utilidad) ? accumulator : accumulator + utilidad;
//     }, 0);

//     return parseFloat(totalUtilidad.toFixed(2));
//   });

export const calcularUtilidadesPorQuincenaDagPacket = (enviosPorQuincena) =>
  Object.values(enviosPorQuincena).map((envios) => {
    if (envios.length === 0) {
      return 0;
    }

    const totalUtilidad = envios.reduce((accumulator, envio) => {
      let utilidad = envio.utilidadEnvioDagpacket ? envio.utilidadEnvioDagpacket : envio.utilidad * 0.3;

      // If empaquetadoDetalle is not null and it has a utility property, add it to the current utility
      if (envio.empaquetadoDetalle && typeof envio.empaquetadoDetalle.utility !== 'undefined') {
        utilidad += envio.empaquetadoDetalle.utility * 0.3;
      }

      return Number.isNaN(utilidad) ? accumulator : accumulator + utilidad;
    }, 0);

    return parseFloat(totalUtilidad.toFixed(2));
  });

/**
 * Calcula las utilidades por quincena a partir de los envíos.
 *
 * @param {Object} enviosPorQuincena - Un objeto que contiene los envíos agrupados por quincena.
 * @returns {Array} Un array con las utilidades calculadas por quincena.
 */

export const calcularUtilidadesPorQuincenaLicenciatario = (enviosPorQuincena) => {
  let a;
  return Object.entries(enviosPorQuincena).map(([key, envios]) => {
    const filteredEnvios = envios.filter((envio) => envio.status === 'CREATED');

    if (filteredEnvios.length === 0) {
      return 0;
    }

    const utilidades = filteredEnvios.reduce((accumulator, envio) => {
      let utilidad =
        envio.utilidadEnvioLicen !== null && envio.utilidadEnvioLicen !== undefined
          ? envio.utilidadEnvioLicen
          : envio.utilidad * 0.7;

      if (envio.empaquetadoDetalle) {
        utilidad += envio.empaquetadoDetalle.utility * 0.7;
      }
      // console.log({ key, accumulator, utilidad }); // Now prints the key as well

      return Number.isNaN(utilidad) ? accumulator : accumulator + utilidad;
    }, 0);

    return parseFloat(utilidades.toFixed(2));
  });
};

export const groupDataByQuincena = (data) => {
  if (data.length === 0) {
    return {};
  }
  const filteredData = data.filter((item) => item.status === 'CREATED');

  const enviosByQuincena = filteredData.reduce((accumulator, envio) => {
    const envioDate = envio.created_at.toDate();
    const year = envioDate.getFullYear();
    const month = envioDate.getMonth();
    const day = envioDate.getDate();

    const quincena = day <= 15 ? '1' : '2';
    const key = `${year}-${month}-${quincena}`;

    if (!accumulator[key]) {
      accumulator[key] = [];
    }

    accumulator[key].push(envio);

    return accumulator;
  }, {});

  const minDate = data.reduce((min, envio) => {
    const envioDate = envio.created_at.toDate();
    return envioDate < min ? envioDate : min;
  }, new Date());

  const maxDate = data.reduce((max, envio) => {
    const envioDate = envio.created_at.toDate();
    return envioDate > max ? envioDate : max;
  }, new Date());

  let currentDate = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate() <= 15 ? 1 : 16);

  while (currentDate <= maxDate) {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const day = currentDate.getDate();
    const quincena = day <= 15 ? '1' : '2';
    const key = `${year}-${month}-${quincena}`;

    if (!enviosByQuincena[key]) {
      enviosByQuincena[key] = [];
    }

    currentDate = new Date(year, month + (quincena === '1' ? 0 : 1), day === 1 ? 16 : 1);
  }

  // Sort the keys of enviosByQuincena in descending order (newer to older)
  const sortedKeys = Object.keys(enviosByQuincena).sort((a, b) => b.localeCompare(a));

  // Create a new object with the sorted keys
  const sortedEnviosByQuincena = {};
  sortedKeys.forEach((key) => {
    sortedEnviosByQuincena[key] = enviosByQuincena[key];
  });

  return sortedEnviosByQuincena;
};

export const groupDataByQuincenaOtros = (data) => {
  if (data.length === 0) {
    return {};
  }
  // const filteredData = data.filter((item) => item.status === 'CREATED');

  const enviosByQuincena = data.reduce((accumulator, envio) => {
    const envioDate = envio.created_at.toDate();
    const year = envioDate.getFullYear();
    const month = envioDate.getMonth();
    const day = envioDate.getDate();

    const quincena = day <= 15 ? '1' : '2';
    const key = `${year}-${month}-${quincena}`;

    if (!accumulator[key]) {
      accumulator[key] = [];
    }

    accumulator[key].push(envio);

    return accumulator;
  }, {});

  const minDate = data.reduce((min, envio) => {
    const envioDate = envio.created_at.toDate();
    return envioDate < min ? envioDate : min;
  }, new Date());

  const maxDate = data.reduce((max, envio) => {
    const envioDate = envio.created_at.toDate();
    return envioDate > max ? envioDate : max;
  }, new Date());

  let currentDate = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate() <= 15 ? 1 : 16);

  while (currentDate <= maxDate) {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const day = currentDate.getDate();
    const quincena = day <= 15 ? '1' : '2';
    const key = `${year}-${month}-${quincena}`;

    if (!enviosByQuincena[key]) {
      enviosByQuincena[key] = [];
    }

    currentDate = new Date(year, month + (quincena === '1' ? 0 : 1), day === 1 ? 16 : 1);
  }

  // Sort the keys of enviosByQuincena in descending order (newer to older)
  const sortedKeys = Object.keys(enviosByQuincena).sort((a, b) => b.localeCompare(a));

  // Create a new object with the sorted keys
  const sortedEnviosByQuincena = {};
  sortedKeys.forEach((key) => {
    sortedEnviosByQuincena[key] = enviosByQuincena[key];
  });

  return sortedEnviosByQuincena;
};

export const groupDataByQuincenaByUpdate = (data) => {
  if (data.length === 0) {
    return {};
  }

  const enviosByQuincena = data.reduce((accumulator, envio) => {
    if (!envio.updated_at) {
      return accumulator;
    }
    const envioDate = envio.updated_at.toDate();
    const year = envioDate.getFullYear();
    const month = envioDate.getMonth();
    const day = envioDate.getDate();

    const quincena = day <= 15 ? '1' : '2';
    const key = `${year}-${month}-${quincena}`;

    if (!accumulator[key]) {
      accumulator[key] = [];
    }

    accumulator[key].push(envio);

    return accumulator;
  }, {});

  const minDate = data.reduce((min, envio) => {
    const envioDate = envio.updated_at.toDate();
    return envioDate < min ? envioDate : min;
  }, new Date());

  const maxDate = data.reduce((max, envio) => {
    const envioDate = envio.updated_at.toDate();
    return envioDate > max ? envioDate : max;
  }, new Date());

  let currentDate = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate() <= 15 ? 1 : 16);

  while (currentDate <= maxDate) {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const day = currentDate.getDate();
    const quincena = day <= 15 ? '1' : '2';
    const key = `${year}-${month}-${quincena}`;

    if (!enviosByQuincena[key]) {
      enviosByQuincena[key] = [];
    }

    currentDate = new Date(year, month + (quincena === '1' ? 0 : 1), day === 1 ? 16 : 1);
  }

  // Sort the keys of enviosByQuincena in descending order (newer to older)
  const sortedKeys = Object.keys(enviosByQuincena).sort((a, b) => b.localeCompare(a));

  // Create a new object with the sorted keys
  const sortedEnviosByQuincena = {};
  sortedKeys.forEach((key) => {
    sortedEnviosByQuincena[key] = enviosByQuincena[key];
  });

  return sortedEnviosByQuincena;
};
/**
 * Calculate utilities by half-month for a licensee.
 *
 * @param {Object} enviosPorQuincena - An object containing shipments grouped by half-month.
 * @returns {Array} An array with utilities calculated by half-month.
 */
export const calcularUtilidadesPorQuincenaGrafica = (enviosPorQuincena) => {
  const halfMonths = Object.keys(enviosPorQuincena);
  const utilities = halfMonths.map((halfMonth) => {
    const envios = enviosPorQuincena[halfMonth];

    if (envios.length === 0) {
      return 0;
    }

    const utilidades = envios.reduce(
      (accumulator, envio) => (Number.isNaN(envio.utilidad) ? accumulator : accumulator + envio.utilidad),
      0
    );

    return Number((utilidades * 0.7).toFixed(2));
  });

  return halfMonths.map((halfMonth, index) => ({
    x: halfMonth,
    y: utilities[index]
  }));
};

/**
 * Calcula la suma de las utilidades por cada período en el objeto empaquesPorQuincena.
 *
 * @param {Object} empaquesPorQuincena - Un objeto que contiene los empaques agrupados por quincena.
 * @returns {Object} Un objeto con la suma de las utilidades calculadas por quincena.
 */
export const sumUtilitiesByPeriodEmpaques = (empaquesPorQuincena, isLicenciatario = false) => {
  const utilitiesByPeriod = {};

  Object.entries(empaquesPorQuincena).forEach(([period, arrayOfEmpaques]) => {
    let utilitySum = 0;

    if (arrayOfEmpaques.length > 0) {
      arrayOfEmpaques.forEach((empaque) => {
        if (empaque.items) {
          empaque.items.forEach((item) => {
            const itemUtility = item.utlity ? item.utlity : 0;
            utilitySum += itemUtility;
          });
        }
      });
    }

    utilitiesByPeriod[period] = isLicenciatario ? utilitySum * 0.7 : utilitySum;
  });

  return utilitiesByPeriod;
};

export const groupDataByQuincenaEnvios = (data) => {
  if (data.length === 0) {
    return {};
  }
  const filteredData = data.filter((item) => item.status === 'CREATED');

  const enviosByQuincena = filteredData.reduce((accumulator, envio) => {
    const envioDate = envio.created_at.toDate();
    const year = envioDate.getFullYear();
    const month = envioDate.getMonth();
    const day = envioDate.getDate();

    const quincena = day <= 15 ? '1' : '2';
    const key = `${year}-${month}-${quincena}`;

    if (!accumulator[key]) {
      accumulator[key] = [];
    }

    accumulator[key].push(envio);

    return accumulator;
  }, {});

  const minDate = data.reduce((min, envio) => {
    const envioDate = envio.created_at.toDate();
    return envioDate < min ? envioDate : min;
  }, new Date());

  const maxDate = data.reduce((max, envio) => {
    const envioDate = envio.created_at.toDate();
    return envioDate > max ? envioDate : max;
  }, new Date());

  let currentDate = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate() <= 15 ? 1 : 16);

  while (currentDate <= maxDate) {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const day = currentDate.getDate();
    const quincena = day <= 15 ? '1' : '2';
    const key = `${year}-${month}-${quincena}`;

    if (!enviosByQuincena[key]) {
      enviosByQuincena[key] = [];
    }

    currentDate = new Date(year, month + (quincena === '1' ? 0 : 1), day === 1 ? 16 : 1);
  }

  // Sort the keys of enviosByQuincena in descending order (newer to older)
  const sortedKeys = Object.keys(enviosByQuincena).sort((a, b) => b.localeCompare(a));

  // Create a new object with the sorted keys
  const sortedEnviosByQuincena = {};
  sortedKeys.forEach((key) => {
    sortedEnviosByQuincena[key] = enviosByQuincena[key];
  });

  return sortedEnviosByQuincena;
};

/**
 * Calcula la utilidad de los repartos para cada conjunto de datos.
 *
 * @param {Array.<Array.<Object>>} dataArrays - Un array de arrays, donde cada array interno representa un conjunto de repartos.
 * @returns {Array.<number>} Un array de utilidades calculadas. Cada elemento del array de salida corresponde a la utilidad del respectivo array interno del input.
 *
 * Cada objeto 'reparto' en los arrays internos debe tener una propiedad 'utilidadLicenc' y 'totalAPagar'.
 * - Si 'utilidadLicenc' existe y no es nulo, se suma a la utilidad total.
 * - Si 'utilidadLicenc' no existe o es nulo, entonces 'totalAPagar' * 0.7 se suma a la utilidad total.
 */
export const calculaUtilidadesRepartos = (dataArrays, porcentajeUtilidadUsuario) => {
  // utilidadUsuario = utilidadUsuario || UTILIDAD_POR_DEFECTO;
  const utilidades = dataArrays.map((data) =>
    data.reduce((acc, reparto) => {
      // Si reparto.utilidadLicenc existe y no es null, se añade al acumulador
      if (reparto.utilidadLicenc !== undefined && reparto.utilidadLicenc !== null) {
        return acc + reparto.utilidadLicenc;
      }
      // Si reparto.utilidadLicenc no existe o es null, se añade reparto.totalAPagar * 0.7

      return acc + reparto.totalAPagar * (porcentajeUtilidadUsuario || UTILIDAD_POR_DEFECTO);
    }, 0)
  );

  return utilidades;
};
